import path from 'path';
import fs from 'fs-extra';
import { exec } from 'child_process';
import { promisify } from 'util';
import yaml from 'js-yaml';
import { Workflow, WorkflowState } from '../types';
import { logger } from '../utils/logger';
import { parseMarkdown } from '../utils/markdown';

const execAsync = promisify(exec);

export class WorkflowRunner {
  private projectRoot: string;
  private workflowDir: string;

  constructor(projectRoot: string = process.cwd()) {
    this.projectRoot = projectRoot;
    this.workflowDir = path.join(projectRoot, '.workflow');
  }

  async runCommand(commandName: string): Promise<void> {
    const commandPath = path.join(this.workflowDir, 'commands', `${commandName}.md`);
    
    if (!await fs.pathExists(commandPath)) {
      throw new Error(`Command "${commandName}" not found. Available: init, todo, context, review`);
    }

    logger.info(`🚀 Running command: ${commandName}`);
    logger.info('');
    
    const content = await fs.readFile(commandPath, 'utf-8');
    const sections = parseMarkdown(content);
    
    for (const section of sections) {
      this.processSection(section);
    }
  }

  private processSection(section: any, indent = 0): void {
    const prefix = '  '.repeat(indent);
    logger.info(`${prefix}${'#'.repeat(section.level)} ${section.title}`);
    
    for (const line of section.content) {
      // Check for code blocks
      if (line.trim().startsWith('```')) {
        continue;
      }
      
      // Check for shell commands in code blocks
      const inCodeBlock = section.content.some((l: string, i: number) => 
        i < section.content.indexOf(line) && l.trim().startsWith('```') &&
        !section.content.slice(i + 1, section.content.indexOf(line)).some((l: string) => l.trim().startsWith('```'))
      );
      
      if (inCodeBlock && line.trim()) {
        this.executeCommand(line.trim());
      } else if (line.trim()) {
        logger.info(`${prefix}${line}`);
      }
    }
    
    section.subsections.forEach((sub: any) => this.processSection(sub, indent + 1));
  }

  private async executeCommand(command: string): Promise<void> {
    if (command.startsWith('#') || command.startsWith('//')) {
      return; // Skip comments
    }
    
    logger.info(`  $ ${command}`);
    
    try {
      const { stdout, stderr } = await execAsync(command, { 
        cwd: this.projectRoot,
        timeout: 30000 // 30 second timeout
      });
      
      if (stdout) {
        stdout.split('\n').forEach(line => {
          if (line.trim()) logger.info(`    ${line}`);
        });
      }
      
      if (stderr && !stderr.includes('warning')) {
        stderr.split('\n').forEach(line => {
          if (line.trim()) logger.warn(`    ${line}`);
        });
      }
    } catch (error: any) {
      logger.error(`    Command failed: ${error.message}`);
    }
  }

  async startWorkflow(type: string, options: { name?: string; description?: string } = {}): Promise<void> {
    const workflowPath = path.join(this.workflowDir, 'workflows', `${type}.yaml`);
    
    if (!await fs.pathExists(workflowPath)) {
      const available = await this.listAvailableWorkflows();
      throw new Error(`Workflow "${type}" not found. Available: ${available.join(', ')}`);
    }

    // Load workflow
    const workflowContent = await fs.readFile(workflowPath, 'utf-8');
    const workflow = yaml.load(workflowContent) as Workflow;

    logger.info(`🔄 Starting ${workflow.name}`);
    if (workflow.description) {
      logger.info(`   ${workflow.description}`);
    }
    logger.info('');

    // Create workflow state
    const state: WorkflowState = {
      type,
      name: options.name || `${type}-${Date.now()}`,
      startedAt: new Date(),
      currentPhase: 0,
      phases: workflow.phases.map(phase => ({
        name: phase.name,
        status: 'pending',
        completedTasks: []
      }))
    };

    await this.saveState(state);

    // Show first phase
    logger.info(`📋 Phase 1/${workflow.phases.length}: ${workflow.phases[0].name}`);
    logger.info('');
    workflow.phases[0].tasks.forEach((task, index) => {
      logger.info(`  ${index + 1}. ${task}`);
    });
    logger.info('');
    logger.info('Use "workflow status" to track progress');
    logger.info('Use "workflow complete" to mark tasks as done');
  }

  async getStatus(): Promise<WorkflowState | null> {
    const statePath = path.join(this.workflowDir, '.current-workflow.json');
    
    if (!await fs.pathExists(statePath)) {
      return null;
    }

    const state = await fs.readJson(statePath);
    state.startedAt = new Date(state.startedAt);
    
    return state;
  }

  async listAvailableWorkflows(): Promise<string[]> {
    const workflowsDir = path.join(this.workflowDir, 'workflows');
    
    if (!await fs.pathExists(workflowsDir)) {
      return [];
    }

    const files = await fs.readdir(workflowsDir);
    return files
      .filter(f => f.endsWith('.yaml') || f.endsWith('.yml'))
      .map(f => f.replace(/\.(yaml|yml)$/, ''));
  }

  private async saveState(state: WorkflowState): Promise<void> {
    const statePath = path.join(this.workflowDir, '.current-workflow.json');
    await fs.writeJson(statePath, state, { spaces: 2 });
  }
}